import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Star, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewSubmissionPage = () => {
  const { userType, userId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  
  const [formData, setFormData] = useState({
    reviewer_name: '',
    reviewer_email: '',
    rating: 0,
    review_text: '',
    service_type: '',
    date_of_service: ''
  });
  
  const [hoverRating, setHoverRating] = useState(0);

  useEffect(() => {
    fetchUserInfo();
    if (userType === 'provider') {
      fetchServiceTypes();
    }
  }, [userType, userId]);

  const fetchServiceTypes = async () => {
    try {
      const res = await axios.get(`${API}/public/service-types`);
      setServiceTypes(res.data);
    } catch (error) {
      console.error('Failed to fetch service types:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      if (userType === 'agent' || userType === 'broker') {
        const res = await axios.get(`${API}/public/agent/${userId}`);
        setUser({
          name: res.data.name,
          type: userType,
          role: userType
        });
      } else if (userType === 'provider') {
        const res = await axios.get(`${API}/public/provider/${userId}`);
        setUser({
          name: res.data.name,
          type: 'provider',
          service_type: res.data.service_type
        });
        if (res.data.service_type) {
          setFormData(prev => ({ ...prev, service_type: res.data.service_type }));
        }
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setUser({
        name: userType.charAt(0).toUpperCase() + userType.slice(1),
        type: userType
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (!formData.reviewer_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.review_text.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/${userType}/${userId}`, formData);
      
      setSubmitted(true);
      toast.success('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-green-100 rounded-full">
              <CheckCircle size={48} className="text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-2">Thank You!</h2>
          <p className="text-stone-600 mb-6">
            Your review has been submitted successfully. We appreciate your feedback!
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="w-full bg-[#1A4D2E]"
          >
            Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="p-6 md:p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
              Leave a Review
            </h1>
            <p className="text-stone-600">
              Share your experience with <span className="font-semibold">{user?.name}</span>
            </p>
            {user?.type === 'provider' && user?.service_type && (
              <p className="text-sm text-purple-600 font-medium mt-1">{user.service_type}</p>
            )}
            {(user?.type === 'agent' || user?.type === 'broker') && (
              <p className="text-sm text-[#1A4D2E] font-medium mt-1">
                {user?.type === 'broker' ? 'Real Estate Broker' : 'Real Estate Agent'}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating */}
            <div>
              <Label className="mb-2 block">Rating *</Label>
              <div className="flex gap-2 justify-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData({...formData, rating: star})}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      size={40}
                      className={`${
                        star <= (hoverRating || formData.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-stone-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {formData.rating > 0 && (
                <p className="text-center text-sm text-stone-600 mt-2">
                  {formData.rating} out of 5 stars
                </p>
              )}
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name">Your Name *</Label>
              <Input
                id="name"
                value={formData.reviewer_name}
                onChange={(e) => setFormData({...formData, reviewer_name: e.target.value})}
                placeholder="John Doe"
                required
              />
            </div>

            {/* Email (Optional) */}
            <div>
              <Label htmlFor="email">Email (Optional)</Label>
              <Input
                id="email"
                type="email"
                value={formData.reviewer_email}
                onChange={(e) => setFormData({...formData, reviewer_email: e.target.value})}
                placeholder="john@example.com"
              />
              <p className="text-xs text-stone-500 mt-1">
                Your email will not be displayed publicly
              </p>
            </div>

            {/* Service Type */}
            {userType === 'provider' ? (
              <div>
                <Label htmlFor="service">Service Type</Label>
                <Select value={formData.service_type || undefined} onValueChange={(val) => setFormData({...formData, service_type: val})} data-testid="service-type-select">
                  <SelectTrigger data-testid="service-type-select">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Professional Type</Label>
                <div className="mt-1 px-3 py-2 bg-stone-100 rounded-lg text-stone-700 text-sm" data-testid="professional-type-label">
                  {userType === 'broker' ? 'Real Estate Broker' : 'Real Estate Agent'}
                </div>
              </div>
            )}

            {/* Date of Service */}
            <div>
              <Label htmlFor="date">Date of Service (Optional)</Label>
              <Input
                id="date"
                type="date"
                value={formData.date_of_service}
                onChange={(e) => setFormData({...formData, date_of_service: e.target.value})}
              />
            </div>

            {/* Review Text */}
            <div>
              <Label htmlFor="review">Your Review *</Label>
              <Textarea
                id="review"
                value={formData.review_text}
                onChange={(e) => setFormData({...formData, review_text: e.target.value})}
                placeholder="Share your experience..."
                rows={5}
                required
              />
              <p className="text-xs text-stone-500 mt-1">
                Minimum 10 characters
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1A4D2E]"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>

            <p className="text-xs text-center text-stone-500">
              By submitting this review, you agree that it may be displayed publicly
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ReviewSubmissionPage;
